import React, { useCallback, useEffect, useState } from "react";
import ProfileContext from "./ProfileContext";
import { wordApi } from "src/api/word-api";
import { setOutlineMode, setSelectedProfile } from "src/features/review-slice";
import { useAppDispatch, useAppSelector } from "src/hooks";
import {
  useClassifyDocumentMutation,
  useLazyGetFlattenedStylesQuery,
  useLazyGetStyleProfileQuery,
} from "src/api/server-api";
import { UnavailableStylesError } from "src/services/errors/style-service-errors";
import {
  applyClassifiedStyles,
  enableTrackChanges,
  extractDocumentLines,
  getDocumentFileName,
  lockDocument,
  unlockDocument,
} from "src/services/document-services";
import { SELECTED_PROFILE_SETTING_KEY, saveDocumentSetting } from "src/services/settings-service";
import { validateProfileAgainstDocument } from "src/services/style-service";
import { applyCharacterStyles } from "src/services";
import { createCharStyleRecords } from "src/services/character_styles/character-style-history-service";
import { getLocalIdToTagMap, persistCharStyleRecords } from "src/services";
import {
  createStyleRecords,
  updateRecordsFromClassification,
} from "src/services/paragraph-style-history-service";
import {
  captureStylingRunCompleted,
  clearReviewProgress,
  flattenLinksToPlainText,
  formatEta,
  getTaggingPromise,
  beginStylingRun,
  persistStyleRecords,
  startRun,
} from "src/services";
import { trackStylingRun } from "src/services/telemetry";

import type { RadioGroupOnChangeData } from "@fluentui/react-components";
import type { ApplyProgress, PerfReport, PerfSession, StylingRunStats } from "src/services";
import type { ClassificationProgressEvent } from "src/types";

const onUpdateProgress =
  (
    setProgress: (
      progress: Pick<ClassificationProgressEvent, "completed" | "total"> | null
    ) => void,
    setClassificationStatus: (status: string | null) => void,
    perf?: PerfSession
  ) =>
  (event: ClassificationProgressEvent) => {
    const { stage, completed, total } = event;

    // Update numeric progress for the ProgressBar
    setProgress(completed != null || total != null ? { completed, total } : null);

    perf?.note(`classify:${stage ?? "unknown"}`, {
      ...(completed != null ? { completed } : {}),
      ...(total != null ? { total } : {}),
    });

    // Map pipeline stage to a human-readable status message
    switch (stage) {
      case "pre_processing":
        setClassificationStatus("Pre-processing manuscript...");
        break;
      case "chunking":
        setClassificationStatus("Splitting document into chunks...");
        break;
      case "processing": {
        const detail = completed != null && total != null ? ` (${completed}/${total})` : "";
        setClassificationStatus(`Classifying chunks${detail}...`);
        break;
      }
      case "refining":
        setClassificationStatus("Refining classifications...");
        break;
      case "post_processing":
        setClassificationStatus("Post-processing results...");
        break;
      default:
        setClassificationStatus("Classifying document with AI...");
    }
  };

/**
 * Turn an apply-stage step report into the overlay's bar and status line.
 *
 * Single-unit steps get no count — "(0/1)" then "(1/1)" is noise — but they are
 * still worth naming, since one of them is a read that reached 19.6s on a
 * heavily-revised manuscript.
 */
const onApplyProgress =
  (
    setProgress: (
      progress: Pick<ClassificationProgressEvent, "completed" | "total"> | null
    ) => void,
    updateStatus: (status: string | null, eta?: string | null) => void
  ) =>
  ({ step, completed, total, etaMs }: ApplyProgress) => {
    const countable = total > 1;
    setProgress(countable ? { completed, total } : null);
    const count = countable ? ` (${completed.toLocaleString()}/${total.toLocaleString()})` : "";
    updateStatus(`${step}${count}${etaMs != null ? "" : "..."}`, etaMs != null ? formatEta(etaMs) : null);
  };

interface ProfileProviderProps extends React.PropsWithChildren {}

export function ProfileProvider(props: ProfileProviderProps) {
  const dispatch = useAppDispatch();
  const selectedProfile = useAppSelector((state) => state.review.selectedProfile);
  const [getStyleDefinitions] = useLazyGetFlattenedStylesQuery();
  const [getStyleProfile] = useLazyGetStyleProfileQuery();
  const [classifyDocument] = useClassifyDocumentMutation();

  // The radio selection only stages a profile name; it isn't committed
  // (validated, applied to Redux, or persisted) until the user acts on it
  // by clicking "Review Styles" or "Run Styling Assistant".
  const [stagedProfileName, setStagedProfileName] = useState<string | null>(null);

  // Keep the staged (radio) selection in sync with whatever profile is
  // actually committed in Redux — e.g. after restoring a saved profile on
  // load — so navigating back to the styling page shows the current
  // profile selected instead of blank.
  useEffect(() => {
    setStagedProfileName(selectedProfile?.name ?? null);
  }, [selectedProfile]);

  const [classificationStatus, setClassificationStatus] = useState<string | null>(null);
  const [classificationEta, setClassificationEta] = useState<string | null>(null);
  const updateStatus = useCallback((status: string | null, eta: string | null = null) => {
    setClassificationStatus(status);
    setClassificationEta(eta);
  }, []);
  const [isClassifying, setIsClassifying] = useState<boolean>(false);
  const [lockExistingStyles, setLockExistingStyles] = useState<boolean>(false);
  const [pendingSelection, setPendingSelection] = useState<string | null>(null);
  const [progress, setProgress] = useState<Pick<
    ClassificationProgressEvent,
    "completed" | "total"
  > | null>(null);
  const [perfReport, setPerfReport] = useState<PerfReport | null>(null);

  const handleSetProfile = useCallback(
    (ev: React.FormEvent<HTMLDivElement>, data: RadioGroupOnChangeData) => {
      setStagedProfileName(data.value);
    },
    []
  );

  /**
   * Validates the staged profile against the document, applies it to Redux,
   * and persists it into the document file via document settings. Called
   * when the user clicks "Review Styles" or "Run Styling Assistant" — the
   * radio selection alone does not commit a profile.
   *
   * @returns The committed profile name, or `null` if no profile is staged
   *   or validation failed.
   */
  const commitProfile = useCallback(async (): Promise<string | null> => {
    if (!stagedProfileName) {
      return null;
    }

    // Drop any status text left over from a previous run — it outlives
    // `clearErrors`, so a stale "Error: ..." would otherwise be re-rendered as
    // a plain status message for as long as this commit takes.
    setClassificationStatus(null);
    setPendingSelection(stagedProfileName);

    try {
      const { data: styleDefinitions } = await getStyleDefinitions(stagedProfileName);

      if (!styleDefinitions || styleDefinitions.length === 0) {
        throw Error("Server returned empty or null for style definitions.");
      }

      await validateProfileAgainstDocument(styleDefinitions);

      dispatch(setSelectedProfile(stagedProfileName));
      await getStyleProfile(stagedProfileName);
      await saveDocumentSetting(SELECTED_PROFILE_SETTING_KEY, stagedProfileName);

      return stagedProfileName;
    } catch (err) {
      console.error("commitProfile: ", err);
      // UnavailableStylesError is surfaced by the caller against the profile
      // selector; any other error gets a status message here for the caller
      // to report as a generic failure.
      if (err instanceof Error && !(err instanceof UnavailableStylesError)) {
        setClassificationStatus(err.message);
      }
      throw err;
    } finally {
      setPendingSelection(null);
    }
  }, [stagedProfileName, dispatch, getStyleDefinitions, getStyleProfile]);

  const handleClassifyDocument = useCallback(
    (onStylingComplete?: () => void) => async (profileName?: string) => {
      let currentStep = "initializing";
      const perf = startRun("StylingRun");
      // Filled in as the run progresses and reported to Sentry in `finally`, so
      // a run that throws still describes how far it got. "aborted" covers the
      // early returns below, which are neither a success nor a failure.
      const runStats: StylingRunStats = {
        outcome: "aborted",
        lockExistingStyles,
        runId: beginStylingRun(),
        documentName: getDocumentFileName() ?? undefined,
      };
      try {
        // Prefer the name passed in by the caller (e.g. the value just
        // returned from commitProfile). The selectedProfile from Redux may
        // still be stale in this tick because the setSelectedProfile dispatch
        // hasn't re-rendered yet — relying on it would make a first-click run
        // bail out here on a freshly opened document.
        const activeProfileName = profileName ?? selectedProfile?.name;
        if (!activeProfileName) return;
        runStats.profile = activeProfileName;

        setIsClassifying(true);

        // A fresh styling run supersedes any prior review session: drop the
        // saved review location/filters and return the outline to style mode.
        await clearReviewProgress();
        dispatch(setOutlineMode("style"));

        await perf.time("persist:clearPara", () => persistStyleRecords([]));
        currentStep = "persistCharStyleRecords (clear)";
        await perf.time("persist:clearChar", () => persistCharStyleRecords([]));

        setClassificationStatus("Preparing document...");

        currentStep = "getTaggingPromise";
        await perf.time("taggingPromise", getTaggingPromise);

        const reportApplyProgress = onApplyProgress(setProgress, updateStatus);

        // Flatten links before track changes is enabled, so removing them is a
        // silent edit rather than hundreds of revisions for a reviewer to wade
        // through, and before extraction, so the text sent for classification and
        // every offset derived from it already contain no links.
        currentStep = "flattenLinks";
        await perf.time("stage:flattenLinks", () =>
          flattenLinksToPlainText(perf, reportApplyProgress)
        );
        setProgress(null);

        currentStep = "enableTrackChanges";
        await perf.time("enableTrackChanges", () => enableTrackChanges(setClassificationStatus));

        setClassificationStatus("Locking document...");

        currentStep = "lockDocument";
        await perf.time("lock:insert", lockDocument);

        setClassificationStatus("Extracting document content...");

        currentStep = "extractDocumentLines";
        const lines = await extractDocumentLines(perf);
        if (lines.length === 0) {
          setClassificationStatus("No content found in document.");
          return;
        }
        runStats.paragraphs = lines.length;
        currentStep = "getLocalIdToTagMap";
        const localIdToTag = await perf.time("getLocalIdToTagMap", getLocalIdToTagMap, () => ({
          lines: lines.length,
        }));
        const records = createStyleRecords(lines, localIdToTag);
        currentStep = "persistStyleRecords (records)";
        await perf.time(
          "persist:initialPara",
          () => persistStyleRecords(records),
          () => ({
            records: records.length,
          })
        );

        setClassificationStatus("Classifying document with AI...");

        currentStep = "classifyDocument";
        const response = await perf.time("classifyDocument", () =>
          classifyDocument({
            paragraphs: lines,
            profile: activeProfileName,
            title: "",
            bisac: "",
            onUpdateProgress: onUpdateProgress(setProgress, setClassificationStatus, perf),
            lockExistingStyles,
            runId: runStats.runId,
          })
        );

        setProgress(null);

        if (!response.data) {
          throw Error("Classification failed");
        }

        if (!response.data.lines) {
          throw Error("API returned no classified lines.");
        }
        runStats.classifiedLines = response.data.lines.length;

        const updated = updateRecordsFromClassification(records, response.data);
        currentStep = "persistStyleRecords (updated)";
        await perf.time(
          "persist:updatedPara",
          () => persistStyleRecords(updated),
          () => ({
            records: updated.length,
          })
        );

        setClassificationStatus("Applying styles to document...");

        // Unlock before the apply stages: `cannotEdit` blocks programmatic
        // format changes, so paragraph styles will not commit while it is set.
        // This ran as a nested Word.run inside applyClassifiedStyles, where
        // deleting the body-spanning lock invalidated every Range proxy the
        // outer context held.
        currentStep = "unlockBeforeApply";
        await perf.time("lock:removeBeforeApply", unlockDocument);

        currentStep = "applyClassifiedStyles";
        const paragraphStyleResult = await perf.time("stage:applyParagraphStyles", () =>
          applyClassifiedStyles(response.data!, perf, reportApplyProgress)
        );
        runStats.stylesApplied = paragraphStyleResult.applied;
        runStats.stylesSkipped = paragraphStyleResult.skipped;

        setClassificationStatus("Applying character styles...");
        setProgress(null);

        currentStep = "applyCharacterStyles";
        const characterStyleResult = await perf.time("stage:applyCharacterStyles", () =>
          applyCharacterStyles(
            response.data!,
            activeProfileName,
            paragraphStyleResult.formattingSnapshot,
            localIdToTag,
            perf,
            reportApplyProgress
          )
        );

        // The apply steps own the bar; what follows has no unit to count.
        setProgress(null);
        setClassificationStatus("Saving style records...");

        const charStyleRecords = characterStyleResult.actionRecords.flatMap((r) =>
          createCharStyleRecords(
            r.paragraphTag,
            r.paragraphId,
            r.actions,
            r.formattingInfo,
            r.paragraphStyle,
            characterStyleResult.characterStyles
          )
        );
        currentStep = "persistCharStyleRecords";
        await perf.time(
          "persist:charStyles",
          () => persistCharStyleRecords(charStyleRecords),
          () => ({ records: charStyleRecords.length })
        );
        runStats.charStyleRecords = charStyleRecords.length;

        let finalMessage = `✓ Complete! Classified ${response.data.lines.length} paragraphs`;
        if (paragraphStyleResult.applied > 0) {
          finalMessage += `, applied ${paragraphStyleResult.applied} style change${paragraphStyleResult.applied !== 1 ? "s" : ""}`;
        }
        const warning =
          paragraphStyleResult.skipped > 0
            ? `${paragraphStyleResult.skipped} paragraph${paragraphStyleResult.skipped !== 1 ? "s were" : " was"} skipped during this process.`
            : null;
        setClassificationStatus(finalMessage);
        runStats.outcome = "success";

        onStylingComplete?.();
        return { warning };
      } catch (err) {
        runStats.outcome = "error";
        runStats.failedStep = currentStep;
        console.error(`Error triggering styling (step: ${currentStep}):`, err);
        console.error("[styling:error] name:", (err as Error)?.name);
        console.error("[styling:error] message:", (err as Error)?.message);
        console.error("[styling:error] stack:", (err as Error)?.stack);
        console.error(
          "[styling:error] full error object:",
          JSON.stringify(err, Object.getOwnPropertyNames(err))
        );
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        setClassificationStatus(`Error: ${errorMessage}`);
        throw err;
      } finally {
        dispatch(
          wordApi.util.invalidateTags(["character-style-records", "paragraph-style-records"])
        );
        setIsClassifying(false);
        await perf.time("lock:remove", unlockDocument);
        const report = perf.finish();
        setPerfReport(report);
        // Two sinks, one description of the run. App Insights aggregates across
        // runs (percentiles per step), Sentry reads a single run as a waterfall;
        // `runStats.runId` is in both, so one leads to the other.
        captureStylingRunCompleted(report, runStats);
        trackStylingRun(report, runStats);
      }
    },
    [selectedProfile, lockExistingStyles, classifyDocument, dispatch, updateStatus]
  );

  const isRunning = isClassifying || Boolean(pendingSelection);

  const contextValue = {
    classificationStatus,
    classificationEta,
    isClassifying,
    isRunning,
    lockExistingStyles,
    pendingSelection,
    perfReport,
    progress,
    stagedProfileName,
    commitProfile,
    handleClassifyDocument,
    handleSetProfile,
    setLockExistingStyles,
  };

  return <ProfileContext.Provider value={contextValue}>{props.children}</ProfileContext.Provider>;
}
